import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  Users,
  FileText,
  Upload,
  Image,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const AdminCoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [docenti, setDocenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(null);
  const imageInputRef = useRef(null);
  const videoInputRefs = {
    viso: useRef(null),
    corpo: useRef(null),
    braccia_mani: useRef(null),
    gambe_piedi: useRef(null),
    addome: useRef(null),
    testa_collo: useRef(null),
  };

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    duration: "",
    level: "Base",
    product_line: "ODR standard",
    modules: "",
    schedule: "",
    instructor: "",
    instructor_notes: "",
    image_url: "",
    date: "",
    time: "",
    max_participants: 20,
    video_viso: "",
    video_corpo: "",
    video_braccia_mani: "",
    video_gambe_piedi: "",
    video_addome: "",
    video_testa_collo: "",
  });

  const videoZones = [
    { key: "video_viso", label: "Viso", icon: "👤" },
    { key: "video_corpo", label: "Corpo", icon: "🧍" },
    { key: "video_braccia_mani", label: "Braccia e Mani", icon: "🤲" },
    { key: "video_gambe_piedi", label: "Gambe e Piedi", icon: "🦶" },
    { key: "video_addome", label: "Addome", icon: "⭕" },
    { key: "video_testa_collo", label: "Testa e Collo", icon: "🗣️" },
  ];

  useEffect(() => {
    fetchCourses();
    fetchDocenti();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`);
      setCourses(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei corsi");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocenti = async () => {
    try {
      const response = await axios.get(`${API}/docenti`);
      setDocenti(response.data);
    } catch (error) {
      console.error("Errore nel caricamento dei docenti:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const fullUrl = `${window.location.origin}${response.data.url}`;
      setFormData({ ...formData, image_url: fullUrl });
      toast.success("Immagine caricata con successo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (file, videoKey) => {
    if (!file) return;

    setUploadingVideo(videoKey);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const fullUrl = `${window.location.origin}${response.data.url}`;
      setFormData({ ...formData, [videoKey]: fullUrl });
      toast.success("Video caricato con successo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'upload del video");
    } finally {
      setUploadingVideo(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      duration: "",
      level: "Base",
      product_line: "ODR standard",
      modules: "",
      schedule: "",
      instructor: "",
      instructor_notes: "",
      image_url: "",
      date: "",
      time: "",
      max_participants: 20,
      video_viso: "",
      video_corpo: "",
      video_braccia_mani: "",
      video_gambe_piedi: "",
      video_addome: "",
      video_testa_collo: "",
    });
    setEditingCourse(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (course) => {
    setEditingCourse(course);
    setFormData({
      title: course.title,
      description: course.description,
      duration: course.duration,
      level: course.level,
      product_line: course.product_line,
      modules: course.modules.join("\n"),
      schedule: course.schedule,
      instructor: course.instructor,
      instructor_notes: course.instructor_notes || "",
      image_url: course.image_url || "",
      date: course.date,
      time: course.time,
      max_participants: course.max_participants,
      video_viso: course.video_viso || "",
      video_corpo: course.video_corpo || "",
      video_braccia_mani: course.video_braccia_mani || "",
      video_gambe_piedi: course.video_gambe_piedi || "",
      video_addome: course.video_addome || "",
      video_testa_collo: course.video_testa_collo || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const courseData = {
      ...formData,
      modules: formData.modules.split("\n").filter((m) => m.trim()),
      max_participants: parseInt(formData.max_participants),
    };

    try {
      if (editingCourse) {
        await axios.put(`${API}/admin/courses/${editingCourse.course_id}`, courseData);
        toast.success("Corso aggiornato con successo");
      } else {
        await axios.post(`${API}/admin/courses`, courseData);
        toast.success("Corso creato con successo");
      }
      setDialogOpen(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!courseToDelete) return;

    try {
      await axios.delete(`${API}/admin/courses/${courseToDelete.course_id}`);
      toast.success("Corso eliminato");
      fetchCourses();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
    }
  };

  const getLevelBadgeClass = (level) => {
    switch (level) {
      case "Base":
        return "badge-base";
      case "Intermedio":
        return "badge-intermedio";
      case "Avanzato":
        return "badge-avanzato";
      default:
        return "";
    }
  };

  return (
    <PageLayout>
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Gestione Corsi
            </h1>
            <p className="text-muted-foreground">
              Crea, modifica ed elimina i corsi disponibili
            </p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="rounded-full mt-4 md:mt-0"
            data-testid="create-course-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Corso
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-secondary rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : courses.length > 0 ? (
          <div className="space-y-4">
            {courses.map((course) => (
              <Card key={course.course_id} className="rounded-2xl border-border/50">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-serif text-lg font-medium">{course.title}</h3>
                        <Badge className={`${getLevelBadgeClass(course.level)} border-0`}>
                          {course.level}
                        </Badge>
                        <Badge variant="secondary" className="rounded-full">
                          {course.product_line}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(course.date).toLocaleDateString("it-IT")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {course.duration}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {course.current_participants}/{course.max_participants} iscritti
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {course.materials.length} materiali
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={() => openEditDialog(course)}
                        data-testid={`edit-course-${course.course_id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Modifica
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setCourseToDelete(course);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-course-${course.course_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <p className="text-muted-foreground mb-4">Nessun corso disponibile</p>
              <Button onClick={openCreateDialog} className="rounded-full">
                Crea il primo corso
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingCourse ? "Modifica Corso" : "Nuovo Corso"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="title">Titolo *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                  data-testid="course-title-input"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="description">Descrizione *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="level">Livello *</Label>
                <Select
                  value={formData.level}
                  onValueChange={(v) => handleSelectChange("level", v)}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Base">Base</SelectItem>
                    <SelectItem value="Intermedio">Intermedio</SelectItem>
                    <SelectItem value="Avanzato">Avanzato</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="product_line">Linea Prodotti *</Label>
                <Select
                  value={formData.product_line}
                  onValueChange={(v) => handleSelectChange("product_line", v)}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ODR standard">ODR standard</SelectItem>
                    <SelectItem value="ODR Clean">ODR Clean</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Data *</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Ora Inizio *</Label>
                <Input
                  id="time"
                  name="time"
                  type="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Durata *</Label>
                <Input
                  id="duration"
                  name="duration"
                  placeholder="es. 3h"
                  value={formData.duration}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="schedule">Orario *</Label>
                <Input
                  id="schedule"
                  name="schedule"
                  placeholder="es. 09:00 - 12:00"
                  value={formData.schedule}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="instructor">Docente *</Label>
                {docenti.length > 0 ? (
                  <Select
                    value={formData.instructor}
                    onValueChange={(v) => handleSelectChange("instructor", v)}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="instructor-select">
                      <SelectValue placeholder="Seleziona docente" />
                    </SelectTrigger>
                    <SelectContent>
                      {docenti.map((docente) => (
                        <SelectItem key={docente.docente_id} value={docente.name}>
                          {docente.name}
                          {docente.specialization && (
                            <span className="text-muted-foreground ml-2">
                              ({docente.specialization})
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="instructor"
                    name="instructor"
                    value={formData.instructor}
                    onChange={handleChange}
                    required
                    className="rounded-xl"
                    placeholder="Nome docente"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_participants">Max Partecipanti *</Label>
                <Input
                  id="max_participants"
                  name="max_participants"
                  type="number"
                  min="1"
                  value={formData.max_participants}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="modules">Moduli (uno per riga) *</Label>
                <Textarea
                  id="modules"
                  name="modules"
                  value={formData.modules}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Modulo 1&#10;Modulo 2&#10;Modulo 3"
                  className="rounded-xl"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label>Immagine del Corso</Label>
                <div className="flex items-start gap-4">
                  <div className="w-40 h-28 rounded-xl bg-secondary flex items-center justify-center overflow-hidden border-2 border-dashed border-border">
                    {formData.image_url ? (
                      <img 
                        src={formData.image_url} 
                        alt="Anteprima" 
                        className="w-full h-full object-cover object-center"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Image className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files[0])}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? "Caricamento..." : "Carica Immagine"}
                    </Button>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p><strong>Formati:</strong> JPG, PNG, GIF, WEBP</p>
                      <p><strong>Dimensione consigliata:</strong> 800x600px o superiore</p>
                      <p><strong>Max:</strong> 10MB</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Oppure inserisci URL:</p>
                    <Input
                      id="image_url"
                      name="image_url"
                      type="url"
                      value={formData.image_url}
                      onChange={handleChange}
                      placeholder="https://..."
                      className="rounded-xl"
                    />
                  </div>
                </div>
              </div>

              {/* Video Esempi per Zone del Corpo */}
              <div className="md:col-span-2 space-y-4">
                <Label className="text-base font-medium">Video Esempi per Zone del Corpo</Label>
                <p className="text-xs text-muted-foreground">
                  Carica mini video dimostrativi per ogni zona. <strong>Formati:</strong> MP4, MOV, WEBM (max 50MB)
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {videoZones.map((zone) => (
                    <div key={zone.key} className="p-3 border border-border rounded-xl space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{zone.icon}</span>
                        <span className="text-sm font-medium">{zone.label}</span>
                      </div>
                      {formData[zone.key] ? (
                        <div className="space-y-2">
                          <video 
                            src={formData[zone.key]} 
                            className="w-full h-20 object-cover rounded-lg bg-black"
                            controls
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full text-destructive text-xs"
                            onClick={() => setFormData({ ...formData, [zone.key]: "" })}
                          >
                            Rimuovi
                          </Button>
                        </div>
                      ) : (
                        <>
                          <input
                            ref={videoInputRefs[zone.key.replace("video_", "")]}
                            type="file"
                            accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                            className="hidden"
                            onChange={(e) => handleVideoUpload(e.target.files[0], zone.key)}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full rounded-lg text-xs"
                            onClick={() => videoInputRefs[zone.key.replace("video_", "")]?.current?.click()}
                            disabled={uploadingVideo === zone.key}
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            {uploadingVideo === zone.key ? "Caricamento..." : "Carica Video"}
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="instructor_notes">Note Docente</Label>
                <Textarea
                  id="instructor_notes"
                  name="instructor_notes"
                  value={formData.instructor_notes}
                  onChange={handleChange}
                  rows={2}
                  className="rounded-xl"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => setDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="rounded-full"
                disabled={saving}
                data-testid="save-course-btn"
              >
                {saving ? "Salvataggio..." : editingCourse ? "Salva Modifiche" : "Crea Corso"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina corso</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare "{courseToDelete?.title}"?
              Questa azione eliminerà anche tutte le iscrizioni e i materiali associati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default AdminCoursesPage;
